import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import {
  UseStratificationRulesProps,
  UseStratificationRulesReturn,
} from './types'
import { useMemo } from 'react'

export const useStratificationRules = (
  props?: UseStratificationRulesProps,
): UseStratificationRulesReturn => {
  const {
    consultationsQnt,
    performedConsultationsQnt,
    hasCurrentCicle,
    answeredQuiz,
    lastValidHealthyAnswer,
    healthyStratificationNotification,
  } = props
  const { clinic } = useAuthContext()

  const hasMentalHealth =
    clinic?.flag?.HabilitarEstratificacaoSaudeMental &&
    clinic?.services?.servicoPsicologia?.atendimentoAgendado

  const canShowQuestionary =
    clinic?.flag?.HabilitarEstratificacaoSaudeMental &&
    clinic?.services?.servicoPsicologia?.atendimentoAgendado &&
    clinic?.flag?.HabilitarInstrumentoPaciente &&
    !!clinic?.idTypeForm

  const mentalHealthProgress = useMemo(() => {
    if (consultationsQnt === null || consultationsQnt === undefined) return
    if (
      performedConsultationsQnt === null ||
      performedConsultationsQnt === undefined
    )
      return
    if (!consultationsQnt || !performedConsultationsQnt) return 0
    return (performedConsultationsQnt / consultationsQnt) * 100
  }, [consultationsQnt, performedConsultationsQnt])

  const isLastConsultationAndNotAnswered = useMemo(() => {
    if (answeredQuiz === null || answeredQuiz === undefined) return
    if (consultationsQnt === null || consultationsQnt === undefined) return
    if (
      performedConsultationsQnt === null ||
      performedConsultationsQnt === undefined
    )
      return
    return (
      performedConsultationsQnt === consultationsQnt - 1 &&
      answeredQuiz === false
    )
  }, [consultationsQnt, performedConsultationsQnt, answeredQuiz])

  const remindToAnswerQuestionary = useMemo(() => {
    if (
      !hasCurrentCicle &&
      healthyStratificationNotification &&
      clinic?.flag?.HabilitarInstrumentoPaciente
    )
      return true
    if (hasCurrentCicle === undefined) return
    if (
      healthyStratificationNotification === undefined ||
      lastValidHealthyAnswer === undefined
    )
      return
    if (!clinic?.flag?.HabilitarInstrumentoPaciente || !hasMentalHealth)
      return false
    if (healthyStratificationNotification && lastValidHealthyAnswer) return true
    if (
      !clinic?.flag?.HabilitarInstrumentoPaciente ||
      clinic?.flag?.PodeAgendarConsultaSemCicloDeTratamento
    )
      return false
    if (!hasCurrentCicle && lastValidHealthyAnswer) return false
    return isLastConsultationAndNotAnswered || !hasCurrentCicle
  }, [
    isLastConsultationAndNotAnswered,
    hasCurrentCicle,
    healthyStratificationNotification,
    lastValidHealthyAnswer,
  ])

  const needsToAnswerQuestionary = useMemo(() => {
    return (
      remindToAnswerQuestionary &&
      clinic?.flag?.HabilitarInstrumentoPacienteObrigatorio
    )
  }, [
    clinic?.flag?.HabilitarInstrumentoPacienteObrigatorio,
    remindToAnswerQuestionary,
  ])

  return {
    hasMentalHealth,
    mentalHealthProgress,
    isLastConsultationAndNotAnswered,
    canShowQuestionary,
    remindToAnswerQuestionary,
    needsToAnswerQuestionary,
  }
}
